import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { printResultDocument, downloadResultPdf, DOC_THEMES } from "@/lib/resultDocument";
import OpticalSheet from "@/components/OpticalSheet";
import { Loader2, Printer, ScanLine, Eye, EyeOff, Clock, FileCheck2, FileDown, Trash2 } from "lucide-react";

export default function OpticalResults({ studentId, studentName, track, readOnly }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printId, setPrintId] = useState("");
  const [openSheetExamId, setOpenSheetExamId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/optical-results/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);
  useEffect(() => { if (studentId) load(); }, [load, studentId]);

  const doPrint = async (r) => {
    setPrintId(r.id);
    try {
      const { data } = await api.get(`/optical-results/${studentId}/document/${r.id}`);
      const ok = await printResultDocument(data);
      if (!ok) toast.error("Belge penceresi açılamadı. Pop-up iznini kontrol edin.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPrintId("");
    }
  };

  const approve = async (r, approved) => {
    try {
      await api.put(`/optical-exams/${r.exam_id}/approve`, { student_ids: [studentId], approved });
      toast.success(approved ? "Onaylandı — sonuç belgesi oluşturuldu" : "Onay kaldırıldı");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.exam_name}" sonucu bu öğrenciden silinecek (Genel/Branş Deneme ve PPD kayıtları dahil). Emin misiniz?`)) return;
    try {
      await api.delete(`/optical-results/${r.id}`);
      toast.success("Sınav sonucu silindi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="optical-results-panel">
      {/* Title */}
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
          <ScanLine size={20} className="text-teal-700" /> Sınav Sonuçları (Optik Okuma & Karneler)
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Kurumun değerlendirdiği sınav sonuçlarınız onaylandığında türüne göre (TYT / AYT Sayısal / Eşit Ağırlık / Sözel) resmi sonuç belgeniz otomatik oluşturulur.
        </p>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="animate-spin inline" size={24} />
        </div>
      ) : items.length === 0 ? (
        <div className="card-ta p-10 text-center text-slate-400">
          Henüz adınıza onaylanmış bir sınav sonuç belgesi bulunmuyor.
        </div>
      ) : (
        <div className="space-y-4" data-testid="opt-result-list">
          {items.map((r) => {
            const isSheetOpen = openSheetExamId === r.id;
            const subs = r.per_subject || [];
            const t = r.totals || {};
            const theme = DOC_THEMES[r.doc_type] || null;

            return (
              <div
                key={r.id}
                className="card-ta p-5 space-y-4 border border-slate-200/90 hover:shadow-sm transition-shadow"
                data-testid={`opt-result-${r.id}`}
              >
                {/* Result Summary Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-extrabold px-2.5 py-0.5 rounded-full border ${
                          r.exam_type?.includes("AYT")
                            ? "bg-amber-100 text-amber-700 border-amber-200"
                            : r.exam_type?.includes("Branş")
                            ? "bg-purple-100 text-purple-700 border-purple-200"
                            : "bg-blue-100 text-blue-700 border-blue-200"
                        }`}
                      >
                        {r.exam_type}
                      </span>
                      <h4 className="font-display font-bold text-slate-900 text-base truncate">
                        {r.exam_name}
                      </h4>
                      {r.approved ? (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white flex items-center gap-1" style={{ background: theme?.ac || "#0f766e" }} data-testid={`opt-approved-${r.id}`}>
                          <FileCheck2 size={11} /> {r.doc_type} Belgesi Hazır
                        </span>
                      ) : (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1" data-testid={`opt-pending-${r.id}`}>
                          <Clock size={11} /> Onay Bekliyor
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">
                      Sınav Tarihi: <span className="font-semibold text-slate-600">{r.date}</span>
                      {r.approved && r.score ? <> · Tahmini Puan: <span className="font-bold text-slate-700">{Number(r.score).toFixed(2)}</span></> : null}
                    </div>
                  </div>

                  {/* Net KPI & Action buttons */}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Toplam Net</div>
                      <div className="font-display text-2xl font-extrabold text-teal-700 leading-none">
                        {Number(t.net || 0).toFixed(2)}
                      </div>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenSheetExamId(isSheetOpen ? null : r.id)}
                      className="h-9 border-slate-300 text-slate-700"
                      data-testid={`opt-view-sheet-${r.id}`}
                    >
                      {isSheetOpen ? <EyeOff size={14} /> : <Eye size={14} />}
                      <span className="ml-1.5">{isSheetOpen ? "Optiği Gizle" : "Optik Formu Gör"}</span>
                    </Button>

                    {!readOnly && (
                      r.approved ? (
                        <Button size="sm" variant="outline" onClick={() => approve(r, false)} className="h-9 border-amber-300 text-amber-700 text-xs" data-testid={`opt-unapprove-${r.id}`}>
                          Onayı Kaldır
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => approve(r, true)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-xs font-bold" data-testid={`opt-approve-${r.id}`}>
                          <FileCheck2 size={14} /><span className="ml-1.5">Onayla & Belge Oluştur</span>
                        </Button>
                      )
                    )}

                    <Button
                      size="sm"
                      onClick={() => doPrint(r)}
                      disabled={printId === r.id || (!r.approved && readOnly)}
                      className="h-9 bg-slate-900 hover:bg-slate-800 text-xs font-bold"
                      data-testid={`opt-print-${r.id}`}
                    >
                      {printId === r.id ? <Loader2 className="animate-spin" size={14} /> : <Printer size={14} />}
                      <span className="ml-1.5">{r.approved ? `${r.doc_type} Belgesi` : "Belge Önizle"}</span>
                    </Button>
                    {r.approved && (
                      <Button size="sm" variant="outline" disabled={printId === r.id + "pdf"} className="h-9 text-xs font-bold" data-testid={`opt-pdf-${r.id}`}
                        onClick={async () => { setPrintId(r.id + "pdf"); try { const { data } = await api.get(`/optical-results/${studentId}/document/${r.id}`); await downloadResultPdf(data); } catch (e) { toast.error(apiErr(e)); } finally { setPrintId(""); } }}>
                        {printId === r.id + "pdf" ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />}<span className="ml-1.5">PDF İndir</span>
                      </Button>
                    )}
                    {!readOnly && (
                      <Button size="sm" variant="outline" onClick={() => remove(r)} className="h-9 text-xs font-bold border-rose-300 text-rose-600 hover:bg-rose-50" data-testid={`opt-delete-${r.id}`}>
                        <Trash2 size={14} /><span className="ml-1.5">Sil</span>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Subject Net Badges */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  {subs.map((s) => (
                    <div
                      key={s.name}
                      className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/80 text-center space-y-0.5"
                      data-testid={`opt-chip-${r.id}-${s.name}`}
                    >
                      <div className="font-bold text-xs text-slate-800 truncate">{s.name}</div>
                      <div className="text-sm font-extrabold text-indigo-700">
                        {Number(s.net).toFixed(2)} <span className="text-[10px] text-slate-400 font-normal">net</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-semibold flex items-center justify-center gap-1.5 pt-0.5">
                        <span className="text-emerald-700">{s.dc}D</span>
                        <span className="text-rose-600">{s.yc}Y</span>
                        <span className="text-slate-400">{s.blank}B</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Interactive Optical Sheet & Kazanım Tables Viewer */}
                {isSheetOpen && (
                  <div className="pt-2 border-t border-slate-100 space-y-6" data-testid={`opt-sheet-viewer-${r.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Öğrenci 5 Şıklı Optik İşaretleme ve Kazanım Başarı Analizi:
                      </span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1 text-emerald-700 font-semibold">
                          <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" /> Doğru (D)
                        </span>
                        <span className="flex items-center gap-1 text-rose-700 font-semibold">
                          <span className="w-3 h-3 rounded-full bg-rose-600 inline-block" /> Yanlış (Y)
                        </span>
                        <span className="flex items-center gap-1 text-amber-700 font-semibold">
                          <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Boş (B)
                        </span>
                      </div>
                    </div>

                    {subs.map((s) => {
                      const studentAnswers = r.answers?.[s.name] || "";
                      const marksArr = String(s.marks || "").split("");

                      return (
                        <div key={s.name} className="space-y-3 bg-slate-50/50 p-4 rounded-2xl border border-slate-200">
                          {/* 5-option bubble sheet */}
                          <OpticalSheet
                            title={`${s.name} Optik Formu (${s.dc} Doğru · ${s.yc} Yanlış · ${s.blank} Boş · Net: ${Number(s.net).toFixed(2)})`}
                            questionCount={s.ss}
                            value={studentAnswers}
                            readOnly={true}
                            kazanimlar={s.kazanimlar}
                          />

                          {/* Detailed Kazanım Question Table for this Subject */}
                          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2">
                            <div className="text-xs font-bold text-slate-800 flex items-center justify-between">
                              <span>{s.name} — Soru & Kazanım Değerlendirme Tablosu</span>
                              <span className="text-xs text-indigo-700 font-semibold">Net: {Number(s.net).toFixed(2)}</span>
                            </div>

                            <div className="max-h-60 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-slate-200 text-left bg-slate-50 text-slate-500">
                                    <th className="py-2 px-2.5 w-12 text-center">Soru</th>
                                    <th className="py-2 px-2.5">Kazanım</th>
                                    <th className="py-2 px-2.5 w-20 text-center">Öğrenci</th>
                                    <th className="py-2 px-2.5 w-24 text-center">Durum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {marksArr.map((m, i) => {
                                    const qNum = i + 1;
                                    const kaz = (s.kazanimlar || [])[i] || `Soru ${qNum}`;
                                    const stu = studentAnswers[i] || "-";

                                    return (
                                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                                        <td className="py-1.5 px-2.5 font-bold text-center text-slate-600">{qNum}</td>
                                        <td className="py-1.5 px-2.5 font-medium text-slate-800">{kaz}</td>
                                        <td className="py-1.5 px-2.5 font-mono font-bold text-center">{stu}</td>
                                        <td className="py-1.5 px-2.5 text-center">
                                          {m === "D" ? (
                                            <span className="inline-block px-2 py-0.5 rounded font-extrabold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
                                              D (Doğru)
                                            </span>
                                          ) : m === "Y" ? (
                                            <span className="inline-block px-2 py-0.5 rounded font-extrabold text-[10px] bg-rose-100 text-rose-800 border border-rose-200">
                                              Y (Yanlış)
                                            </span>
                                          ) : (
                                            <span className="inline-block px-2 py-0.5 rounded font-extrabold text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
                                              B (Boş)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
